import { useState, useCallback, memo } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Section } from './Section';
import { InsertZone } from './InsertZone';
import { useCoverageCalculations } from '../../hooks/useCoverageCalculations';
import { useDocumentNavigation } from '../../hooks/useDocumentNavigation';
import type { DocType, DocumentSection, SectionMetadata, SectionType } from '../../types/document';

interface SectionListProps {
  sections: DocumentSection[];
  metadata: Record<string, SectionMetadata>;
  docType: DocType;
  /** All available upstream anchors for coverage dropdown (PRD anchors for design sections) */
  upstreamAnchors?: string[];
  /** Title lookup for upstream anchors (anchor -> section title) */
  upstreamTitleMap?: Record<string, string>;
  onReorder: (activeKey: string, overKey: string) => void;
  onSave: (sectionKey: string, body: string) => Promise<void>;
  onTitleChange: (sectionKey: string, newTitle: string) => void;
  /** Called when coverage selection changes (design sections) */
  onCoversChange?: (sectionKey: string, covers: string[]) => void;
  onInsert: (afterKey: string | null) => string; // Returns new section key
  onDelete: (sectionKey: string) => void;
  /** Called when a section is selected (for URL state persistence) */
  onSectionSelect?: (sectionKey: string) => void;
  /** Story ID for task navigation display */
  storyId?: string;
  /** Called when user clicks a design capability in PRD coverage indicator */
  onNavigateToDesign?: (anchor: string) => void;
  /** Called when user clicks a task in design coverage indicator */
  onNavigateToTask?: (taskSeq: string) => void;
  /** Called when user clicks a PRD anchor in design covers dropdown */
  onNavigateToPRD?: (anchor: string) => void;
  /** Initial field to activate on mount (for first section body editing) */
  initialActiveField?: { sectionKey: string; field: 'title' | 'body' } | null;
  /** Called when section type changes (Story 250) */
  onSectionTypeChange?: (sectionKey: string, type: SectionType) => void;
}

type ActiveField = { sectionKey: string; field: 'title' | 'body' };

export function SectionList({
  sections,
  metadata,
  docType,
  upstreamAnchors = [],
  upstreamTitleMap = {},
  onReorder,
  onSave,
  onTitleChange,
  onCoversChange,
  onInsert,
  onDelete,
  onSectionSelect,
  storyId,
  onNavigateToDesign,
  onNavigateToTask,
  onNavigateToPRD,
  initialActiveField = null,
  onSectionTypeChange,
}: SectionListProps) {
  const [activeField, setActiveField] = useState<ActiveField | null>(initialActiveField);

  // Memoized coverage calculations (Story 280)
  const { uncoveredAnchors } = useCoverageCalculations(sections, upstreamAnchors);

  // Navigation handlers (Story 280)
  const {
    handleNavigateNextFromBody,
    handleShiftReturnFromBody,
    handleNavigateToTitle,
    handleNavigateToBody,
    handleNavigatePrevFromTitle,
    handleInsert,
  } = useDocumentNavigation({
    sections,
    setActiveField,
    onInsert,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
    // KeyboardSensor removed - was interfering with text navigation in editors
  );

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorder(active.id as string, over.id as string);
    }
  };

  // Stable callbacks for section operations (Story 280)
  const handleActivate = useCallback((sectionKey: string, field: 'title' | 'body') => {
    setActiveField({ sectionKey, field });
  }, []);

  const handleDeactivate = useCallback(() => {
    setActiveField(null);
  }, []);

  const handleSectionSave = useCallback((sectionKey: string, body: string) => {
    return onSave(sectionKey, body);
  }, [onSave]);

  const handleSectionTitleChange = useCallback((sectionKey: string, newTitle: string) => {
    onTitleChange(sectionKey, newTitle);
  }, [onTitleChange]);

  const handleSectionCoversChange = useCallback((sectionKey: string, covers: string[]) => {
    onCoversChange?.(sectionKey, covers);
  }, [onCoversChange]);

  const handleSectionDelete = useCallback((sectionKey: string) => {
    onDelete(sectionKey);
  }, [onDelete]);

  const handleSectionSelect = useCallback((sectionKey: string) => {
    onSectionSelect?.(sectionKey);
  }, [onSectionSelect]);

  const handleSectionTypeChangeCallback = useCallback((sectionKey: string, type: SectionType) => {
    onSectionTypeChange?.(sectionKey, type);
  }, [onSectionTypeChange]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sections.map((s) => s.section_key)}
        strategy={verticalListSortingStrategy}
      >
        {/* Insert zone at the beginning */}
        <InsertZone onInsert={() => handleInsert(null)} />

        {sections.map((section, index) => (
          <div key={section.section_key}>
            <SortableSection
              section={section}
              metadata={section.anchor ? metadata[section.anchor] : undefined}
              docType={docType}
              upstreamAnchors={upstreamAnchors}
              upstreamTitleMap={upstreamTitleMap}
              uncoveredAnchors={uncoveredAnchors}
              activeField={activeField?.sectionKey === section.section_key ? activeField.field : null}
              onActivate={handleActivate}
              onDeactivate={handleDeactivate}
              onSectionSave={handleSectionSave}
              onSectionTitleChange={handleSectionTitleChange}
              onSectionCoversChange={onCoversChange ? handleSectionCoversChange : undefined}
              onSectionDelete={handleSectionDelete}
              onNavigateNextFromBody={handleNavigateNextFromBody}
              onShiftReturnFromBody={handleShiftReturnFromBody}
              onNavigateToTitle={handleNavigateToTitle}
              onNavigateToBody={handleNavigateToBody}
              onNavigatePrevFromTitle={handleNavigatePrevFromTitle}
              onSectionSelect={onSectionSelect ? handleSectionSelect : undefined}
              storyId={storyId}
              onNavigateToDesign={onNavigateToDesign}
              onNavigateToTask={onNavigateToTask}
              onNavigateToPRD={onNavigateToPRD}
              onSectionTypeChangeCallback={onSectionTypeChange ? handleSectionTypeChangeCallback : undefined}
            />
            {/* Insert zone after each section */}
            {index < sections.length - 1 && (
              <InsertZone onInsert={() => handleInsert(section.section_key)} />
            )}
          </div>
        ))}

        {/* Insert zone at the end */}
        {sections.length > 0 && (
          <InsertZone onInsert={() => handleInsert(sections[sections.length - 1].section_key)} />
        )}
      </SortableContext>
    </DndContext>
  );
}

interface SortableSectionProps {
  section: DocumentSection;
  metadata?: SectionMetadata;
  docType: DocType;
  upstreamAnchors: string[];
  upstreamTitleMap: Record<string, string>;
  uncoveredAnchors: Set<string>;
  activeField: 'title' | 'body' | null;
  // Stable callbacks that take sectionKey (Story 280)
  onActivate: (sectionKey: string, field: 'title' | 'body') => void;
  onDeactivate: () => void;
  onSectionSave: (sectionKey: string, body: string) => void;
  onSectionTitleChange: (sectionKey: string, newTitle: string) => void;
  onSectionCoversChange?: (sectionKey: string, covers: string[]) => void;
  onSectionDelete: (sectionKey: string) => void;
  onNavigateNextFromBody: (sectionKey: string) => void;
  onShiftReturnFromBody: (sectionKey: string) => void;
  onNavigateToTitle: (sectionKey: string) => void;
  onNavigateToBody: (sectionKey: string) => void;
  onNavigatePrevFromTitle: (sectionKey: string) => void;
  onSectionSelect?: (sectionKey: string) => void;
  storyId?: string;
  onNavigateToDesign?: (anchor: string) => void;
  onNavigateToTask?: (taskSeq: string) => void;
  onNavigateToPRD?: (anchor: string) => void;
  onSectionTypeChangeCallback?: (sectionKey: string, type: SectionType) => void;
}

const SortableSection = memo(function SortableSection({
  section,
  metadata,
  docType,
  upstreamAnchors,
  upstreamTitleMap,
  uncoveredAnchors,
  activeField,
  onActivate,
  onDeactivate,
  onSectionSave,
  onSectionTitleChange,
  onSectionCoversChange,
  onSectionDelete,
  onNavigateNextFromBody,
  onShiftReturnFromBody,
  onNavigateToTitle,
  onNavigateToBody,
  onNavigatePrevFromTitle,
  onSectionSelect,
  storyId,
  onNavigateToDesign,
  onNavigateToTask,
  onNavigateToPRD,
  onSectionTypeChangeCallback,
}: SortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.section_key });

  const style = {
    // Use Translate instead of Transform to avoid scale distortion
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const sectionKey = section.section_key;

  return (
    <div ref={setNodeRef} style={style} {...attributes} onClick={onSectionSelect ? () => onSectionSelect(sectionKey) : undefined}>
      <Section
        section={section}
        metadata={metadata}
        docType={docType}
        upstreamAnchors={upstreamAnchors}
        upstreamTitleMap={upstreamTitleMap}
        uncoveredAnchors={uncoveredAnchors}
        activeField={activeField}
        isDragging={isDragging}
        onActivateTitle={() => onActivate(sectionKey, 'title')}
        onActivateBody={() => onActivate(sectionKey, 'body')}
        onDeactivate={onDeactivate}
        onSave={(body) => onSectionSave(sectionKey, body)}
        onTitleChange={(newTitle) => onSectionTitleChange(sectionKey, newTitle)}
        onCoversChange={onSectionCoversChange ? (covers) => onSectionCoversChange(sectionKey, covers) : undefined}
        onDelete={() => onSectionDelete(sectionKey)}
        onNavigateNextFromBody={() => onNavigateNextFromBody(sectionKey)}
        onShiftReturnFromBody={() => onShiftReturnFromBody(sectionKey)}
        onNavigateToTitle={() => onNavigateToTitle(sectionKey)}
        onNavigateToBody={() => onNavigateToBody(sectionKey)}
        onNavigatePrevFromTitle={() => onNavigatePrevFromTitle(sectionKey)}
        dragHandleProps={listeners}
        storyId={storyId}
        onNavigateToDesign={onNavigateToDesign}
        onNavigateToTask={onNavigateToTask}
        onNavigateToPRD={onNavigateToPRD}
        onSectionTypeChange={onSectionTypeChangeCallback ? (type) => onSectionTypeChangeCallback(sectionKey, type) : undefined}
      />
    </div>
  );
});
